<template>
  <v-card elevation="0">
    <template v-if="documentForDialog">
      <warehouse-purchase-order-dialog
        v-model="purchaseOrderDialog"
        :editedItem="documentForDialog"
        :warehouse="documentForDialog?.warehouse || {}"
        :is-purchase-order="documentForDialog?.transaction_type === 'order'"
      />
      <warehouse-entry-grocery-dialog
        v-model="purchaseItemDialog"
        :warehouse="documentForDialog?.warehouse || {}"
        :editedItem="documentForDialog"
      />
      <warehouse-waybill-dialog
        v-model="waybillDialog"
        :warehouse="documentForDialog?.warehouse || {}"
        :edited-item="documentForDialog"
      />
      <warehouse-write-off-dialog
        v-model="writeOffDialog"
        :warehouse="documentForDialog?.warehouse || {}"
        :edited-item="documentForDialog"
      />
    </template>

    <!-- Mobile View -->
    <template v-if="device.isMobile">
      <template v-if="filteredTransactions.length > 0 || loading">
        <mobile-data-cards
          v-for="(group, warehouseId) in transactionsByWarehouse"
          :key="warehouseId"
          :headers="mobileHeaders"
          :items="group.items"
          :loading="loading"
          :title="filters.warehouse.length > 1 ? group.warehouseName : undefined"
          item-key="id"
          show-filters
          :fields-per-row="null"
          :show-row-number="false"
          :bold-fields="['grocery.name']"
          @click="openDocumentDialog($event)"
        >
          <template v-slot:item.transaction_type="{ item }">
            <v-chip
              small
              :color="documentTypes[item.transaction_type]?.color"
              text-color="white"
            >
              {{ documentTypes[item.transaction_type]?.title }}
            </v-chip>
          </template>
          <template v-slot:item.created_at="{ item }">
            {{ formatDateTime(item.created_at) }}
          </template>
        </mobile-data-cards>
      </template>

      <empty-state
        v-if="!loading && filteredTransactions.length === 0"
        icon="mdi-history"
        :title="$t('no_transactions')"
        :subtitle="
          lastTransactionDate
            ? $t('warehouse.last_transaction') + ': ' + lastTransactionDate
            : $t('no_transactions_yet')
        "
      />
    </template>

    <!-- Desktop View -->
    <template v-else>
      <!-- Aggregate View -->
      <template v-if="aggregateMode">
        <v-data-table
          v-if="transactionAggregatedItems.length > 0 || loading"
          :headers="transactionAggregateHeaders"
          :items="transactionAggregatedItems"
          :loading="loading"
          class="striped-table ordera-box-shadow"
          hide-default-footer
          :items-per-page="-1"
        >
          <template v-slot:top>
            <v-toolbar flat>
              <span v-if="lastTransactionDate" class="text-caption grey--text text--darken-1">
                {{ $t("warehouse.last_transaction") }}:
                <strong>{{ lastTransactionDate }}</strong>
              </span>
              <v-spacer />
              <v-btn-toggle v-model="aggregateMode" dense>
                <v-btn :value="true" small>
                  <v-icon left small>mdi-sigma</v-icon>
                  {{ $t("warehouse.aggregate") }}
                </v-btn>
              </v-btn-toggle>
            </v-toolbar>
          </template>
          <template v-slot:item.grocery_name="{ item }">
            {{ toLatin(item.grocery_name) || "-" }}
          </template>
          <template v-slot:item.total_quantity="{ item }">
            {{ formatNumber(item.total_quantity) }}
          </template>
        </v-data-table>
        <empty-state
          v-if="!loading && transactionAggregatedItems.length === 0"
          icon="mdi-sigma"
          :title="$t('warehouse.no_aggregate_data')"
          :subtitle="$t('warehouse.no_entries_for_period')"
        />
      </template>

      <!-- Regular Document View -->
      <v-data-table
        v-else-if="filteredTransactions.length > 0 || loading"
        :headers="headers"
        :items="filteredTransactions"
        :loading="loading"
        class="striped-table ordera-box-shadow"
      >
        <template v-slot:top>
          <v-toolbar flat>
            <v-spacer />
            <v-btn-toggle v-model="aggregateMode" dense>
              <v-btn :value="true" small>
                <v-icon left small>mdi-sigma</v-icon>
                {{ $t("warehouse.aggregate") }}
              </v-btn>
            </v-btn-toggle>
          </v-toolbar>
        </template>
        <template v-slot:item.transaction_type="{ item }">
          <td
            :style="{
              backgroundColor: documentTypes[item.transaction_type]?.color,
            }"
          >
            {{ documentTypes[item.transaction_type]?.title }}
          </td>
        </template>
        <template v-slot:item.created_at="{ item }">
          {{ formatDateTime(item.created_at) }}
        </template>
        <template v-slot:item.actions="{ item }">
          <circle-button
            icon="mdi-eye"
            :tooltip="$t('overview')"
            color="primary"
            @click="openDocumentDialog(item)"
          />
        </template>
      </v-data-table>

      <empty-state
        v-if="!aggregateMode && !loading && filteredTransactions.length === 0"
        icon="mdi-history"
        :title="$t('no_transactions')"
        :subtitle="
          lastTransactionDate
            ? $t('warehouse.last_transaction') + ': ' + lastTransactionDate
            : $t('no_transactions_yet')
        "
        @click:subtitle="setFiltersToLastTransaction()"
      />
    </template>
  </v-card>
</template>

<script>
import hasPrinting from "@/mixins/hasPrinting";
import hasExcel from "@/mixins/hasExcel";
import hasWarehousePrinting from "@/mixins/warehouse/hasWarehousePrinting";
import WarehousePurchaseOrderDialog from "@/components/warehouse/dialogs/WarehousePurchaseOrderDialog.vue";
import WarehouseWaybillDialog from "@/components/warehouse/dialogs/WarehouseWaybillDialog.vue";
import WarehouseWriteOffDialog from "@/components/warehouse/dialogs/WarehouseWriteOffDialog.vue";
import WarehouseEntryGroceryDialog from "@/components/warehouse/dialogs/WarehouseEntryGroceryDialog.vue";
import EmptyState from "@/components/global/EmptyState.vue";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import hasSnackbar from "@/mixins/hasSnackbar";
import warehouseService from "@/services/warehouse";
import { isCreatedInDateRange, groupByWarehouse } from "@/utils/warehouseUtils";

const DIALOG_MAP = {
  order: "purchaseOrderDialog",
  request: "purchaseOrderDialog",
  entry: "purchaseItemDialog",
  exit: "waybillDialog",
  write_off: "writeOffDialog",
};

export default {
  mixins: [hasPrinting, hasWarehousePrinting, hasExcel, hasSnackbar],
  props: {
    selectedGrocery: {
      type: Object,
      default: null,
    },
  },
  components: {
    WarehousePurchaseOrderDialog,
    WarehouseWaybillDialog,
    WarehouseWriteOffDialog,
    WarehouseEntryGroceryDialog,
    EmptyState,
    MobileDataCards,
  },
  data() {
    return {
      transactions: [],
      loading: false,
      aggregateMode: false,
      documentForDialog: null,
      purchaseOrderDialog: false,
      purchaseItemDialog: false,
      waybillDialog: false,
      writeOffDialog: false,
      allDocuments: [],
    };
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const res = await warehouseService.getTransactions();
        this.transactions = res.data;
        this.$store.commit("warehouse/SET_TRANSACTIONS", this.transactions);
        const groceries = this.transactions
          .map((item) => item.grocery)
          .filter((g) => g && g.id);
        const unique = Object.values(
          groceries.reduce((map, g) => { if (!map[g.id]) map[g.id] = g; return map; }, {})
        );
        this.$store.commit("warehouse/SET_CODE_FILTER_GROCERIES", unique);
      } catch (error) {
        this.showErrorSnackbar(this.$t("error_loading_transactions"));
      } finally {
        this.loading = false;
      }
    },
    async openDocumentDialog(item) {
      try {
        if (!this.allDocuments.length) {
          const { data } = await warehouseService.getWarehouseDocuments({});
          this.allDocuments = data;
        }
        this.documentForDialog = this.allDocuments.find(
          (doc) => doc.id === item.warehouse_document_id
        );
        if (!this.documentForDialog) {
          this.showErrorSnackbar(this.$t("document_not_found"));
          return;
        }
        const dialogKey = DIALOG_MAP[item.transaction_type];
        if (dialogKey) this[dialogKey] = true;
      } catch (error) {
        this.showErrorSnackbar(this.$t("error_loading_document"));
      }
    },
    formatNumber(val) {
      if (!val) return "";
      return Number(val).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    setFiltersToLastTransaction() {
      if (!this.transactions.length) return;
      const sorted = [...this.transactions].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      const date = sorted[0].created_at.split("T")[0];
      this.$store.commit("warehouse/SET_DATE", [date, date]);
    },
    formatDateTime(date) {
      return this.localeDate(date, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
  computed: {
    translatedTransactions() {
      return this.transactions.map((item) => {
        return {
          ...item,
          warehouse: {
            ...item.warehouse,
            name: this.translate(item.warehouse?.name || ""),
          },
          grocery: {
            ...item.grocery,
            name: this.translate(item.grocery?.name),
            measure: this.translate(item.grocery?.measure),
          },
        };
      });
    },
    filteredTransactions() {
      const { search, documentType, groceryCode, dates, warehouse } = this.filters;
      return this.customSearch(this.translatedTransactions, this.headers, search)
        .filter(
          (item) =>
            (!documentType || item.transaction_type === documentType) &&
            (!groceryCode || item.grocery.code === groceryCode.code) &&
            isCreatedInDateRange(item, dates.from, dates.to) &&
            warehouse.some((w) => w.id === item.warehouse_id)
        )
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    },
    headers() {
      const multiWarehouse = this.filters.warehouse.length > 1;
      return [
        { text: "ID", align: "start", value: "id", mobile: false, width: "100px" },
        {
          text: this.$t("stock"),
          align: "start",
          value: "warehouse.name",
          mobile: false,
          show: multiWarehouse,
        },
        {
          text: this.$t("warehouse.document_number"),
          value: "warehouse_document_id",
          mobile: false,
        },
        { text: this.$t("warehouse.transaction"), value: "transaction_type", cols: 6 },
        { text: this.$t("code"), value: "grocery.code", cols: 4, mobile: false },
        { text: this.$t("warehouse.product"), value: "grocery.name", cols: 6 },
        {
          text: this.$t("quantity"),
          value: "amount",
          cols: 6,
          suffixField: "grocery.measure",
        },
        { text: this.$t("unit_of_measurment"), value: "grocery.measure", mobile: false },
        { text: this.$t("date"), value: "created_at", print: false, cols: 6 },
        {
          text: this.$t("actions"),
          value: "actions",
          sortable: false,
          align: "center",
          mobile: false,
          print: false,
          width: "80px",
        },
      ].filter((h) => h.show !== false);
    },
    mobileHeaders() {
      return this.headers.filter((h) => h.mobile !== false);
    },
    transactionsByWarehouse() {
      return groupByWarehouse(this.filteredTransactions, this.$t("warehouse.unknown"));
    },
    filters() {
      return this.$store.state.warehouse.filters;
    },
    documentTypes() {
      return {
        order: { title: this.$t("warehouse.purchase_order"), color: "#4CAF50" },
        request: { title: this.$t("warehouse.request_quote"), color: "#81C784" },
        entry: {
          title: this.$t("warehouse.warehouse_replenishment"),
          color: this.$vuetify.theme.currentTheme.primary,
        },
        exit: { title: this.$t("warehouse.shipping_bill"), color: "#2196F3" },
        write_off: {
          title: this.$t("warehouse.write_off"),
          color: this.$vuetify.theme.currentTheme.error || "#F44336",
        },
      };
    },
    transactionAggregatedItems() {
      const groceryMap = {};
      this.filteredTransactions.forEach((item) => {
        const g = item.grocery;
        if (!g?.id) return;
        if (!groceryMap[g.id]) {
          groceryMap[g.id] = {
            grocery_name: g.name || "",
            grocery_code: g.code || "",
            measure: g.measure || "",
            total_quantity: 0,
            document_count: 0,
          };
        }
        groceryMap[g.id].total_quantity += Number(item.amount) || 0;
        groceryMap[g.id].document_count++;
      });
      return Object.values(groceryMap).sort(
        (a, b) => (a.grocery_name || "").localeCompare(b.grocery_name || "")
      );
    },
    transactionAggregateHeaders() {
      return [
        { text: this.$t("code"), value: "grocery_code", width: "100px" },
        { text: this.$t("warehouse.product"), value: "grocery_name" },
        {
          text: this.$t("unit_of_measurment"),
          value: "measure",
          width: "90px",
          align: "center",
        },
        {
          text: this.$t("warehouse.total_quantity"),
          value: "total_quantity",
          align: "right",
          width: "130px",
        },
        {
          text: this.$t("warehouse.document_count"),
          value: "document_count",
          align: "center",
          width: "80px",
        },
      ];
    },
    exportItems() {
      if (this.aggregateMode) {
        return this.transactionAggregatedItems.map((item) => ({
          ...item,
          total_quantity: this.formatNumber(item.total_quantity),
        }));
      }
      return this.filteredTransactions.map((item) => ({
        ...item,
        transaction_type: this.documentTypes[item.transaction_type].title,
      }));
    },
    exportTitle() {
      const parts = [
        this.filters.warehouse.map((w) => w.name).join(", ") || this.$t("stock"),
        this.$t("warehouse.transactions"),
      ];
      if (this.aggregateMode) parts.push(this.$t("warehouse.aggregate"));
      return parts.join(" ");
    },
    lastTransactionDate() {
      if (!this.transactions.length) return null;
      const sorted = [...this.transactions].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      return this.localeDate(sorted[0].created_at, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
  },
  watch: {
    "$store.state.warehouse.refreshTrigger"() {
      this.load();
    },
    "filters.dates": {
      handler() {
        this.load();
      },
      deep: true,
      immediate: true,
    },
    isPrinting(val) {
      if (val) {
        this.printDocument({
          title: this.exportTitle,
          subtitle: this.subtitle,
          tables: [{
            headers: this.aggregateMode ? this.transactionAggregateHeaders : this.headers.filter((h) => h.value !== "actions"),
            items: this.exportItems,
          }],
        });
      }
    },
    isExcelDownloading(val) {
      if (val) {
        this.downloadExcel({
          fileName: this.exportTitle + ".xlsx",
          sheets: [{
            name: this.exportTitle,
            headers: this.aggregateMode ? this.transactionAggregateHeaders : this.headers.filter((h) => h.value !== "actions"),
            items: this.exportItems,
            title: this.exportTitle,
            subtitle: this.subtitle,
          }],
        });
      }
    },
  },
};
</script>
